import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BaseTextInputProps } from '../shared-types';
import { noop, useForkRef, useInputComponentIdAndLabelData } from '../utils';
import BaseInputIcon from '../BaseInput/BaseInputIcon';
import InputControl from '../InputControl/InputControl';
import BaseInput from '../BaseInput/BaseInput';
import { SxProp } from '../style/sxProp';
import useTheme from '../style/useTheme';

export type Props = {
  sx?: SxProp;
} & BaseTextInputProps;

const InteractiveCopyField = forwardRef<HTMLInputElement, Props>(
  ({ label, sx, value, 'aria-label': ariaLabel, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>();
    const handleRef: React.Ref<HTMLInputElement> = useForkRef(ref, inputRef);
    const [isCopied, setIsCopied] = useState(false);
    const { resolveColor } = useTheme();

    const handleClick = useCallback(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 5000);
    }, [setIsCopied]);

    const adornmentRight = useMemo(
      () =>
        value ? (
          <BaseInputIcon
            icon={isCopied ? 'checkmarkCircle' : 'copy'}
            onClick={() => {
              navigator.clipboard.writeText(value);
              handleClick();
            }}
          />
        ) : null,
      [value, isCopied, handleClick]
    );

    const {
      id,
      containerId,
      labelId,
      'aria-labelledby': ariaLabelledby,
    } = useInputComponentIdAndLabelData(
      'InteractiveCopyField',
      label,
      ariaLabel
    );

    return (
      <InputControl id={containerId} label={label} labelId={labelId} sx={sx}>
        <BaseInput
          {...props}
          readOnly
          ref={handleRef}
          label={label}
          componentName="InteractiveCopyField"
          value={value}
          error={false}
          adornmentRight={adornmentRight}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          data-testid={id}
          onChange={noop}
          css={{
            backgroundColor: resolveColor({
              type: 'theme',
              colorLegacy: 'Primary/10',
              colorNext: 'brand/surface',
            }),
          }}
        />
      </InputControl>
    );
  }
);

export default InteractiveCopyField;
